'use client';

import { type CurrencyTaxStatus } from '~/context/currency-context';
import { Website } from '~/fecom-api/schema';
import { cn } from '~/lib/utils';
import { formatCurrency } from '~/utils/currency/currency';

type PriceProps = {
  priceIncVat?: number;
  priceExcVat?: number;
  multiplier?: number;
  showVatStatusText?: boolean;
  className?: string;
  vatStatusClassName?: string;
  // Show the opposite VAT status to the current currency status (useful when showing both inc. and exc. VAT prices)
  showOppositeVatStatus?: boolean;
  savingsText?: (isIncVat: boolean) => string | null;
  isPriceBreakPrice?: boolean;
  currency?: string;
  component?: React.ElementType;
  websiteId: Website;
};

export function Price({
  priceExcVat,
  priceIncVat,
  multiplier = 1,
  showVatStatusText,
  className,
  vatStatusClassName,
  showOppositeVatStatus,
  savingsText,
  isPriceBreakPrice = false,
  currency,
  component = 'span',
  websiteId,
}: PriceProps) {
  const Comp = component;

  let isIncVat = websiteId === Website.InternationalMarketplace;

  // reverse the VAT status if showOppositeVatStatus is true
  if (showOppositeVatStatus) {
    isIncVat = !isIncVat;
  }

  if (websiteId === Website.FindelInternational && isIncVat) {
    return false;
  }

  const getPrice = () => {
    return isIncVat ? priceIncVat : priceExcVat;
  };

  const price = getPrice();

  return (
    <Comp
      className={cn(className)}
      data-testid={isIncVat ? 'price-inc-vat' : 'price-exc-vat'}
    >
      {!isIncVat && isPriceBreakPrice ? (
        <span className="text-sm sm:text-lg">From </span>
      ) : null}
      {formatCurrency((price ?? 0) * multiplier, false, currency)}{' '}
      {savingsText ? savingsText(isIncVat) : null}{' '}
      {showVatStatusText && (
        <span className={cn('text-[0.75em] font-normal', vatStatusClassName)}>
          {isIncVat ? 'inc. VAT' : 'exc. VAT'}
        </span>
      )}
    </Comp>
  );
}
